import { current } from 'immer';
import { sdkApi } from '../api';
import { tasksSlice } from './tasksSlice';
import type { TasksSliceState } from './tasksSlice';
import TCClient from 'client';
import type TasksService from 'js-sdk/src/services/TasksService';
import type {
  CounterPartyType,
  CreateTaskPayload,
  Task,
  UpdateTaskPayload,
} from 'js-sdk/src/types/Tasks';
import type { SDKQueryErrorType } from 'types/api';

const {
  setLinkedTask,
  setNextCursor,
  mergeConversationTasks,
  upsertConversationTask,
  replaceConversationTask,
  conversationFetchStarted,
  conversationFetchEnded,
} = tasksSlice.actions;

const TASKS_PAGE_LIMIT = 100;
/** Ids per request and page `limit`; task-management caps both at 200. */
export const MESSAGE_IDS_CHUNK_SIZE = 200;

const COUNTER_PARTY_TYPES: Record<string, CounterPartyType> = {
  user: 'p2p',
  group: 'group',
  distributionList: 'list',
};

/** Maps a conversation's `counterPartyType` to task-management's; undefined (e.g. team) = unsupported. */
export function toCounterPartyType(entityType: string | undefined): CounterPartyType | undefined {
  return entityType ? COUNTER_PARTY_TYPES[entityType] : undefined;
}

export type ConversationTasksArg = {
  organizationId: string;
  counterPartyId: string;
  counterPartyType: CounterPartyType;
  /** Omit for the whole conversation (lazy loading). */
  messageIds?: string[];
};

export function conversationTasksKey({
  organizationId,
  counterPartyId,
  counterPartyType,
}: Omit<ConversationTasksArg, 'messageIds'>): string {
  return `${organizationId}:${counterPartyType}:${counterPartyId}`;
}

type TasksFilter = 'open' | 'completed';
type GetTasksArg = { organizationId: string; filter: TasksFilter };

function getTasksClient(): TasksService {
  return (TCClient as unknown as { tasks: TasksService }).tasks;
}

function cursorKey({ organizationId, filter }: GetTasksArg): string {
  return `${organizationId}:${filter}`;
}

function toSdkQueryError(error: unknown): SDKQueryErrorType {
  return { reason: error instanceof Error ? error.message : String(error), code: 0 };
}

const tasksApi = sdkApi.injectEndpoints({
  endpoints: (build) => ({
    createTask: build.mutation<Task, { organizationId: string; payload: CreateTaskPayload }>({
      queryFn: async ({ organizationId, payload }) => {
        try {
          const data = await getTasksClient().create(organizationId, payload);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          dispatch(upsertConversationTask(task));
          dispatch(
            tasksApi.util.updateQueryData(
              'getTasks',
              { organizationId, filter: 'open' },
              (draft) => {
                draft.unshift(task);
              }
            )
          );
        } catch {
          // Failure is surfaced to the modal via the mutation's error state.
        }
      },
    }),
    completeTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().complete(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        let movedTask: Task | undefined;
        const openPatch = dispatch(
          tasksApi.util.updateQueryData('getTasks', { organizationId, filter: 'open' }, (draft) => {
            const index = draft.findIndex((t) => t.id === taskId);
            if (index !== -1) {
              const [removed] = draft.splice(index, 1);
              movedTask = {
                ...current(removed),
                status: 'completed',
                completed_at: new Date().toISOString(),
              };
            }
          })
        );
        const completedPatch = dispatch(
          tasksApi.util.updateQueryData(
            'getTasks',
            { organizationId, filter: 'completed' },
            (draft) => {
              if (movedTask) draft.push(movedTask);
            }
          )
        );
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          dispatch(replaceConversationTask(task));
        } catch {
          openPatch.undo();
          completedPatch.undo();
        }
      },
    }),
    updateTask: build.mutation<
      Task,
      { organizationId: string; taskId: string; payload: UpdateTaskPayload }
    >({
      queryFn: async ({ organizationId, taskId, payload }) => {
        try {
          const data = await getTasksClient().update(organizationId, taskId, payload);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          dispatch(replaceConversationTask(task));
          (['open', 'completed'] as const).forEach((filter) => {
            dispatch(
              tasksApi.util.updateQueryData('getTasks', { organizationId, filter }, (draft) => {
                const index = draft.findIndex((t) => t.id === taskId);
                if (index !== -1) draft[index] = task;
              })
            );
          });
        } catch {
          // Failure is surfaced via the mutation's error state.
        }
      },
    }),
    reopenTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().reopen(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, taskId }, { dispatch, queryFulfilled }) {
        let movedTask: Task | undefined;
        const completedPatch = dispatch(
          tasksApi.util.updateQueryData(
            'getTasks',
            { organizationId, filter: 'completed' },
            (draft) => {
              const index = draft.findIndex((t) => t.id === taskId);
              if (index !== -1) {
                const [removed] = draft.splice(index, 1);
                movedTask = { ...current(removed), status: 'open', completed_at: null };
              }
            }
          )
        );
        const openPatch = dispatch(
          tasksApi.util.updateQueryData('getTasks', { organizationId, filter: 'open' }, (draft) => {
            if (movedTask) draft.push(movedTask);
          })
        );
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
          dispatch(replaceConversationTask(task));
        } catch {
          completedPatch.undo();
          openPatch.undo();
        }
      },
    }),
    // next_cursor isn't part of the cached query data (the draft is a plain
    // Task[] that createTask/completeTask/etc already splice/push into), so
    // it's tracked separately in the slice, dispatched from inside queryFn
    // (fakeBaseQuery types `meta` as `{}`, so it can't be threaded through
    // onQueryStarted's `queryFulfilled` here).
    getTasks: build.query<Task[], GetTasksArg>({
      queryFn: async ({ organizationId, filter }, { dispatch }) => {
        try {
          const { tasks, paging } = await getTasksClient().list(organizationId, filter, {
            limit: TASKS_PAGE_LIMIT,
          });
          dispatch(
            setNextCursor({
              key: cursorKey({ organizationId, filter }),
              nextCursor: paging.next_cursor,
            })
          );
          return { data: tasks };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
    }),
    fetchMoreTasks: build.mutation<Task[], GetTasksArg>({
      queryFn: async ({ organizationId, filter }, { dispatch, getState }) => {
        try {
          const cursor = (getState() as { tasks: TasksSliceState }).tasks.nextCursorByKey[
            cursorKey({ organizationId, filter })
          ];
          if (!cursor) return { data: [] };
          const { tasks, paging } = await getTasksClient().list(organizationId, filter, {
            limit: TASKS_PAGE_LIMIT,
            cursor,
          });
          dispatch(
            setNextCursor({
              key: cursorKey({ organizationId, filter }),
              nextCursor: paging.next_cursor,
            })
          );
          return { data: tasks };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted({ organizationId, filter }, { dispatch, queryFulfilled }) {
        try {
          const { data: tasks } = await queryFulfilled;
          if (tasks.length > 0) {
            dispatch(
              tasksApi.util.updateQueryData('getTasks', { organizationId, filter }, (draft) => {
                draft.push(...tasks);
              })
            );
          }
        } catch {
          // no-op — surfaced via mutation error state.
        }
      },
    }),
    // Follows next_cursor until exhausted; each page is merged into the slice,
    // since task-management pages over task rows and a message group can span pages.
    fetchConversationTasks: build.mutation<null, ConversationTasksArg>({
      queryFn: async (arg, { dispatch }) => {
        const key = conversationTasksKey(arg);
        dispatch(conversationFetchStarted({ key, messageIds: arg.messageIds }));
        try {
          let cursor: string | undefined;
          do {
            const { messages, paging } = await getTasksClient().listByMessages(arg.organizationId, {
              counter_party_id: arg.counterPartyId,
              counter_party_type: arg.counterPartyType,
              ...(arg.messageIds ? { message_ids: arg.messageIds } : {}),
              limit: MESSAGE_IDS_CHUNK_SIZE,
              ...(cursor ? { cursor } : {}),
            });
            dispatch(mergeConversationTasks({ key, groups: messages }));
            cursor = paging.next_cursor ?? undefined;
          } while (cursor);
          return { data: null };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        } finally {
          dispatch(conversationFetchEnded({ key, messageIds: arg.messageIds }));
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateTaskMutation,
  useCompleteTaskMutation,
  useReopenTaskMutation,
  useUpdateTaskMutation,
  useGetTasksQuery,
  useFetchMoreTasksMutation,
  useFetchConversationTasksMutation,
} = tasksApi;

export { tasksApi };
